import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, X, ChevronRight, ChevronLeft, Loader2, CheckCircle2,
  Building2, Briefcase, Clock, MapPin, ListChecks,
} from "lucide-react";

// Setup wizard. Mounted globally inside AppShell. On a fresh
// company_admin login it first shows a small one-time prompt offering the
// wizard. Acceptance opens a 5-step modal that ends with a single call to
// /api/setup/wizard-complete to seed rules from an industry template. The
// wizard never enables L3/L4 automation.

const INDUSTRIES = [
  { id: "contact_centre", label: "Contact centre",  Icon: Briefcase,
    description: "Inbound and outbound queues, back office, training cycles." },
  { id: "retail",         label: "Retail",          Icon: Building2,
    description: "Shop floor, stockroom and till cover with peak weekends." },
  { id: "warehouse",      label: "Warehouse",       Icon: Building2,
    description: "Picking, packing and dispatch with morning goods-in." },
  { id: "office",         label: "Office",          Icon: Building2,
    description: "Standard weekday cover across admin, finance and support." },
  { id: "healthcare",     label: "Healthcare",      Icon: Briefcase,
    description: "Clinical, reception and admin with on-call rotation." },
];

const SIZE_BANDS = [
  { id: "small",  label: "Small",  hint: "Up to about 25 people" },
  { id: "medium", label: "Medium", hint: "About 26 to 100 people" },
  { id: "large",  label: "Large",  hint: "More than 100 people" },
];

const SHIFT_PATTERNS = [
  { id: "single",     label: "Single shift",
    hint: "One block of cover each day, weekdays only." },
  { id: "two_shift",  label: "Two shifts",
    hint: "Early and late cover, usually 7am to 7pm." },
  { id: "round_clock", label: "Round the clock",
    hint: "24-hour cover including overnight." },
  { id: "weekend",    label: "Includes weekends",
    hint: "Weekday plus Saturday or Sunday cover." },
];

const DAYS = [
  { id: 1, label: "Mon" }, { id: 2, label: "Tue" }, { id: 3, label: "Wed" },
  { id: 4, label: "Thu" }, { id: 5, label: "Fri" }, { id: 6, label: "Sat" },
  { id: 7, label: "Sun" },
];

export default function SetupWizardGate() {
  // V1 customer UI: the setup wizard is paused. Returning null here
  // hides the one-time prompt and the wizard modal from every user.
  // The rest of this function is kept intact so we can re-enable later
  // by simply removing this early return.
  return null;
  /* eslint-disable */
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "company_admin";
  const [status, setStatus] = useState(null);
  const [step, setStep]     = useState(0);       // 0 = closed, 1..5 = steps, 6 = done
  const [busy, setBusy]     = useState(false);

  const refresh = useCallback(async () => {
    if (!isCompanyAdmin) { setStatus(null); return; }
    try {
      const { data } = await api.get("/setup/wizard-status");
      setStatus(data);
    } catch { setStatus(null); }
  }, [isCompanyAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!isCompanyAdmin || !status) return null;
  if (status.completed_at) return null;

  // ── Step 0a, one-time prompt for existing tenants (not yet prompted).
  if (status.should_prompt && step === 0) {
    return (
      <OneTimePrompt
        hasExistingRules={status.has_existing_rules}
        onYes={async () => {
          await api.post("/setup/wizard-prompt-ack", { action: "start" }).catch(() => {});
          setStep(1);
        }}
        onLater={async () => {
          await api.post("/setup/wizard-prompt-ack", { action: "skip" }).catch(() => {});
          await refresh();
        }}
      />
    );
  }

  // ── Steps 1..5, wizard modal.
  if (step >= 1 && step <= 5) {
    return (
      <SetupWizardModal
        step={step}
        setStep={setStep}
        busy={busy}
        setBusy={setBusy}
        onClose={() => setStep(0)}
        onComplete={async () => {
          await refresh();
          setStep(6);
        }}
      />
    );
  }

  // ── Step 6, completion celebration.
  if (step === 6) {
    return <CompletionPanel onClose={() => setStep(0)} />;
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// One-time prompt
// ─────────────────────────────────────────────────────────────────────────

function OneTimePrompt({ hasExistingRules, onYes, onLater }) {
  return (
    <div className="fixed inset-x-0 bottom-6 z-40 flex justify-center pointer-events-none"
         data-testid="setup-wizard-prompt">
      <div className="pointer-events-auto bg-white rounded-lg border border-[#BFD3FB] shadow-xl px-5 py-4 w-[560px] max-w-[92vw] flex items-start gap-3">
        <div className="rounded-full bg-[#DBE7FE] p-2 shrink-0">
          <Sparkles className="h-5 w-5 text-[#1D4ED8]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm text-[#0B1A36]">
            Want to try our new setup wizard?
          </h3>
          <p className="text-xs text-[#4B5773] mt-1">
            {hasExistingRules
              ? "It can generate recommended rules for your business. Your existing rules will be kept untouched and the wizard will skip anything with the same name."
              : "It can generate recommended rules for your business in a few clicks. You can customise everything later."}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={onYes}
                    className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                    data-testid="setup-wizard-prompt-yes">
              Yes, show me
            </Button>
            <Button size="sm" variant="outline" onClick={onLater}
                    data-testid="setup-wizard-prompt-later">
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard modal, single component drives all 5 steps
// ─────────────────────────────────────────────────────────────────────────

function SetupWizardModal({ step, setStep, busy, setBusy, onClose, onComplete }) {
  const [form, setForm] = useState({
    industry: "",
    size_band: "small",
    shift_pattern: "single",
    days: [1, 2, 3, 4, 5],
    open_time: "09:00",
    close_time: "17:30",
    locations: [""],
  });
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/setup/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const recommendedTemplateId = (() => {
    if (!form.industry) return null;
    const match = templates.find(
      (t) => t.industry === form.industry && t.size_band === form.size_band);
    if (match) return match.template_id;
    const fallback = templates.find((t) => t.industry === form.industry);
    return fallback ? fallback.template_id : "office_small";
  })();
  const recommendedTemplate = templates.find((t) => t.template_id === recommendedTemplateId);

  async function loadPreview() {
    if (!recommendedTemplateId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/setup/wizard-complete", {
        template_id: recommendedTemplateId,
        industry:    form.industry,
        size_band:   form.size_band,
        operating_hours: { days: form.days, open: form.open_time, close: form.close_time },
        shift_pattern: form.shift_pattern,
        locations:   form.locations.filter((l) => (l || "").trim()),
        dry_run:     true,
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not generate preview.");
    } finally { setBusy(false); }
  }

  async function commit() {
    setBusy(true);
    try {
      await api.post("/setup/wizard-complete", {
        template_id: recommendedTemplateId,
        industry:    form.industry,
        size_band:   form.size_band,
        operating_hours: { days: form.days, open: form.open_time, close: form.close_time },
        shift_pattern: form.shift_pattern,
        locations:   form.locations.filter((l) => (l || "").trim()),
        dry_run:     false,
      });
      toast.success("Setup complete. Your starter rules are ready to review.");
      onComplete();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Setup could not be completed.");
    } finally { setBusy(false); }
  }

  function next() { setStep(step + 1); }
  function back() { setStep(step - 1); }

  const canAdvance = (() => {
    if (step === 1) return !!form.industry;
    if (step === 2) return form.days.length > 0 && form.open_time && form.close_time;
    if (step === 3) return !!form.shift_pattern;
    if (step === 4) return true; // locations optional
    if (step === 5) return !!preview;
    return false;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         data-testid="setup-wizard-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[780px] max-w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 shrink-0">
          <Sparkles className="h-5 w-5 text-[#2563EB]" />
          <h2 className="font-medium text-base text-[#0B1A36]">Set up NextRota for your business</h2>
          <span className="ml-auto text-xs text-[#64748B]">Step {step} of 5</span>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="setup-wizard-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === 1 && <Step1 form={form} setForm={setForm} />}
          {step === 2 && <Step2 form={form} setForm={setForm} />}
          {step === 3 && <Step3 form={form} setForm={setForm} />}
          {step === 4 && <Step4 form={form} setForm={setForm} />}
          {step === 5 && <Step5 form={form} setForm={setForm}
                                   recommendedTemplate={recommendedTemplate}
                                   preview={preview} busy={busy}
                                   onLoadPreview={loadPreview} />}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
          <Button size="sm" variant="ghost" onClick={onClose}
                  data-testid="setup-wizard-skip">
            I'll do this later
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {step > 1 && (
              <Button size="sm" variant="outline" onClick={back} disabled={busy}
                      data-testid="setup-wizard-back">
                <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
              </Button>
            )}
            {step < 5 && (
              <Button size="sm" onClick={next} disabled={!canAdvance}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                      data-testid="setup-wizard-next">
                Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {step === 5 && (
              <Button size="sm" onClick={commit} disabled={busy || !preview}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="setup-wizard-confirm">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                       : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Generate recommended setup
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step components ─────────────────────────────────────────────────────

function Step1({ form, setForm }) {
  return (
    <div data-testid="setup-wizard-step-1">
      <StepHeader Icon={Building2} title="Tell us about your business"
                  body="We'll use this to suggest a sensible starting point. You can customise everything later." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {INDUSTRIES.map((i) => {
          const active = form.industry === i.id;
          const Icon = i.Icon;
          return (
            <button key={i.id} type="button"
                    onClick={() => setForm({ ...form, industry: i.id })}
                    className={`text-left p-3 rounded border ${active
                      ? "border-[#2563EB] bg-[#EFF4FE]"
                      : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`setup-industry-${i.id}`}>
              <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${active ? "text-[#1D4ED8]" : "text-[#4B5773]"}`} />
                <div className="font-medium text-sm text-[#0B1A36]">{i.label}</div>
              </div>
              <p className="text-xs text-[#4B5773] mt-1 leading-snug">{i.description}</p>
            </button>
          );
        })}
      </div>
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-1">Team size</div>
        <div className="flex gap-2 flex-wrap">
          {SIZE_BANDS.map((s) => (
            <button key={s.id} type="button"
                    onClick={() => setForm({ ...form, size_band: s.id })}
                    className={`px-3 py-1.5 rounded border text-sm ${
                      form.size_band === s.id
                        ? "border-[#2563EB] bg-[#EFF4FE] text-[#1D4ED8]"
                        : "border-slate-200 text-[#4B5773] hover:border-slate-300"}`}
                    data-testid={`setup-size-${s.id}`}>
              {s.label} <span className="text-xs text-[#94A3B8]">· {s.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step2({ form, setForm }) {
  const toggleDay = (d) => {
    const has = form.days.includes(d);
    setForm({ ...form, days: has ? form.days.filter((x) => x !== d) : [...form.days, d].sort() });
  };
  return (
    <div data-testid="setup-wizard-step-2">
      <StepHeader Icon={Clock} title="When are you open?"
                  body="Pick the days you operate and the typical opening times. We'll create coverage rules that match." />
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-1">Operating days</div>
        <div className="flex gap-1.5 flex-wrap">
          {DAYS.map((d) => {
            const active = form.days.includes(d.id);
            return (
              <button key={d.id} type="button" onClick={() => toggleDay(d.id)}
                      className={`px-3 py-1.5 rounded border text-sm w-14 ${
                        active
                          ? "border-[#2563EB] bg-[#EFF4FE] text-[#1D4ED8]"
                          : "border-slate-200 text-[#4B5773] hover:border-slate-300"}`}
                      data-testid={`setup-day-${d.id}`}>
                {d.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-1">Open</div>
          <Input type="time" value={form.open_time}
                 onChange={(e) => setForm({ ...form, open_time: e.target.value })}
                 data-testid="setup-open-time" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-1">Close</div>
          <Input type="time" value={form.close_time}
                 onChange={(e) => setForm({ ...form, close_time: e.target.value })}
                 data-testid="setup-close-time" />
        </div>
      </div>
    </div>
  );
}

function Step3({ form, setForm }) {
  return (
    <div data-testid="setup-wizard-step-3">
      <StepHeader Icon={ListChecks} title="What does a typical day look like?"
                  body="Choose the pattern that matches your operation. If your shifts vary by team you can edit individual rules later." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {SHIFT_PATTERNS.map((p) => {
          const active = form.shift_pattern === p.id;
          return (
            <button key={p.id} type="button"
                    onClick={() => setForm({ ...form, shift_pattern: p.id })}
                    className={`text-left p-3 rounded border ${active
                      ? "border-[#2563EB] bg-[#EFF4FE]"
                      : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`setup-shift-${p.id}`}>
              <div className="font-medium text-sm text-[#0B1A36]">{p.label}</div>
              <p className="text-xs text-[#4B5773] mt-1">{p.hint}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step4({ form, setForm }) {
  const update = (i, val) => {
    const next = [...form.locations];
    next[i] = val;
    setForm({ ...form, locations: next });
  };
  const add = () => setForm({ ...form, locations: [...form.locations, ""] });
  const remove = (i) => setForm({ ...form, locations: form.locations.filter((_, j) => j !== i) });
  return (
    <div data-testid="setup-wizard-step-4">
      <StepHeader Icon={MapPin} title="Where does your team work?"
                  body="Add the locations you operate from, such as offices, depots, client sites or remote. This step is optional, you can add more later." />
      <div className="mt-4 space-y-2 max-w-lg">
        {form.locations.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input placeholder="e.g. London office"
                   value={l} onChange={(e) => update(i, e.target.value)}
                   data-testid={`setup-location-${i}`} />
            {form.locations.length > 1 && (
              <button type="button" onClick={() => remove(i)}
                      className="text-slate-500 hover:text-rose-600"
                      data-testid={`setup-location-remove-${i}`}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={add}
                data-testid="setup-location-add">
          Add another location
        </Button>
      </div>
      <p className="text-[11px] text-[#94A3B8] mt-3">
        Skip this step if you only have one site or work mostly remotely. You can always add locations from the Locations page.
      </p>
    </div>
  );
}

function Step5({ form, recommendedTemplate, preview, busy, onLoadPreview }) {
  const [showWhy, setShowWhy] = useState(false);
  const [details, setDetails] = useState(null);
  useEffect(() => {
    if (!preview) onLoadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedTemplate?.template_id]);
  useEffect(() => {
    if (recommendedTemplate?.template_id) {
      api.get(`/setup/templates/${recommendedTemplate.template_id}`)
        .then(({ data }) => setDetails(data))
        .catch(() => setDetails(null));
    }
  }, [recommendedTemplate?.template_id]);
  if (!recommendedTemplate) {
    return <p className="text-sm text-[#4B5773]">Choose an industry on Step 1 to see a preview.</p>;
  }
  return (
    <div data-testid="setup-wizard-step-5">
      <StepHeader Icon={CheckCircle2} title="Review what we'll create"
                  body="Here's the starter setup we'll generate. Existing items with the same names are kept and skipped." />
      <div className="mt-4 rounded border border-[#BFD3FB] bg-[#F0F6FF] p-4">
        <div className="text-sm font-medium text-[#0B1A36]">
          {recommendedTemplate.label}
        </div>
        <p className="text-xs text-[#4B5773] mt-1">{recommendedTemplate.summary}</p>
      </div>
      {busy && (
        <p className="mt-4 text-sm text-[#4B5773]">
          <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Working out what would be created…
        </p>
      )}
      {preview && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="setup-wizard-preview-counts">
          {Object.entries(preview.counts || {}).map(([k, v]) => (
            <div key={k} className="rounded border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{k.replace(/_/g, " ")}</div>
              <div className="text-lg font-medium text-[#0B1A36]">{v}</div>
            </div>
          ))}
        </div>
      )}
      {preview && (preview.warnings || []).length > 0 && (
        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
             data-testid="setup-wizard-warnings">
          {preview.warnings.length} item{preview.warnings.length === 1 ? "" : "s"} will be skipped because the name already exists. Your existing setup will be kept.
        </div>
      )}

      {details && (
        <div className="mt-4 rounded border border-slate-200 bg-white">
          <button type="button"
                  onClick={() => setShowWhy(!showWhy)}
                  className="w-full px-3 py-2 text-left flex items-center gap-2 text-sm font-medium text-[#0B1A36] hover:bg-slate-50"
                  data-testid="setup-wizard-why-toggle">
            <ChevronRight className={`h-4 w-4 transition-transform ${showWhy ? "rotate-90" : ""}`} />
            Why these defaults?
          </button>
          {showWhy && (
            <div className="px-4 pb-4 pt-1 text-sm text-[#1F2A44] space-y-3"
                 data-testid="setup-wizard-why-body">
              <p className="text-[13px] leading-relaxed">{details.notes_for_admin}</p>
              {(details.coverage || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-[#4B5773]">
                    Coverage rules we'll create ({details.coverage.length})
                  </summary>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    {details.coverage.map((c, i) => (
                      <li key={i} className="text-[#0B1A36]">
                        <strong>{c.name}</strong>
                        {c.description ? <span className="text-[#4B5773]">, {c.description}</span> : null}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(details.leave || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-[#4B5773]">
                    Leave rules we'll create ({details.leave.length})
                  </summary>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    {details.leave.map((l, i) => (
                      <li key={i} className="text-[#0B1A36]">
                        <strong>{l.name}</strong>
                        {l.description ? <span className="text-[#4B5773]">, {l.description}</span> : null}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(details.fairness || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-[#4B5773]">
                    Fairness rules we'll create ({details.fairness.length})
                  </summary>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    {details.fairness.map((f, i) => (
                      <li key={i} className="text-[#0B1A36]">
                        <strong>{f.name}</strong>
                        {f.description ? <span className="text-[#4B5773]">, {f.description}</span> : null}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <p className="text-[11px] text-[#94A3B8] pt-1 border-t border-slate-100">
                You can edit, deactivate or add to any of these rules from the Setup pages after the wizard finishes.
              </p>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-[#94A3B8] mt-3">
        This wizard will only create rules. Automation features stay off until you turn them on from the Automation page.
      </p>
    </div>
  );
}

function StepHeader({ Icon, title, body }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-[#2563EB]" />
        <h3 className="font-medium text-base text-[#0B1A36]">{title}</h3>
      </div>
      <p className="text-sm text-[#4B5773] mt-1">{body}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Completion panel
// ─────────────────────────────────────────────────────────────────────────

function CompletionPanel({ onClose }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         data-testid="setup-wizard-done">
      <div className="bg-white rounded-lg shadow-2xl w-[520px] max-w-full overflow-hidden">
        <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          <h3 className="font-medium text-base text-[#0B1A36]">Your starter setup is ready</h3>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-[#0B1A36]">
          <p>We've added a starter set of rules to your account. Nothing is live yet, automation features remain off until you turn them on yourself.</p>
          <p className="text-xs text-[#4B5773]">Recommended next steps:</p>
          <ul className="text-xs text-[#0B1A36] list-disc list-inside space-y-1">
            <li>Add your team members on the Employees page.</li>
            <li>Review the rules and adjust any that don't match your operation.</li>
            <li>When you're ready, open Automation Readiness to see what's needed to enable Draft Automation.</li>
          </ul>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={onClose}
                  data-testid="setup-wizard-done-close">
            Close
          </Button>
          <Button size="sm" onClick={() => { onClose(); navigate("/app/employees"); }}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                  data-testid="setup-wizard-done-employees">
            Add employees
          </Button>
        </div>
      </div>
    </div>
  );
}
